import { useState, useEffect } from 'react'
import { Building2, TrendingUp, DollarSign, Target, ChevronRight, X, Users, Award, BarChart3 } from 'lucide-react'
import { useFilters } from '../contexts/FilterContext'
import { api } from '../services/api'
import styles from './Empreendimentos.module.css'

interface EmpreendimentoSaude {
  id: number
  nome: string
  tipo_negocio: string
  total_leads: number
  vendas: number
  vgv: number
  taxa_conversao: number
}

interface EmpreendimentoDetalhes {
  id: number
  nome: string
  metricas: {
    total_leads: number
    total_vendas: number
    total_pre_cadastros: number
    vgv: number
    ticket_medio: number
    taxa_conversao: number
  }
  leads_por_situacao: Array<{ situacao: string; quantidade: number }>
  leads_por_origem: Array<{ origem: string; quantidade: number }>
  top_corretores: Array<{ nome: string; vendas: number; vgv: number }>
}

export function Empreendimentos() {
  const { queryParams, filtersVersion } = useFilters()
  const [empreendimentos, setEmpreendimentos] = useState<EmpreendimentoSaude[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [selectedEmp, setSelectedEmp] = useState<EmpreendimentoSaude | null>(null)
  const [empDetalhes, setEmpDetalhes] = useState<EmpreendimentoDetalhes | null>(null)
  const [loadingDetalhes, setLoadingDetalhes] = useState(false)

  useEffect(() => {
    async function loadData() {
      setLoading(true)
      setError(null)
      try {
        const data = await api.getEmpreendimentosSaude(queryParams)
        setEmpreendimentos(data)
      } catch (err) {
        setError('Erro ao carregar empreendimentos')
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [filtersVersion, queryParams])

  const handleCardClick = async (emp: EmpreendimentoSaude) => {
    setSelectedEmp(emp)
    setLoadingDetalhes(true)
    try {
      const detalhes = await api.getEmpreendimentoDetalhes(emp.id, queryParams)
      setEmpDetalhes(detalhes)
    } catch (err) {
      console.error('Erro ao carregar detalhes:', err)
      setEmpDetalhes(null)
    } finally {
      setLoadingDetalhes(false)
    }
  }

  const closeModal = () => {
    setSelectedEmp(null)
    setEmpDetalhes(null)
  }

  const investEmpreendimentos = empreendimentos.filter(e => e.tipo_negocio === 'invest' && e.nome)
  const realizaEmpreendimentos = empreendimentos.filter(e => e.tipo_negocio === 'realiza' && e.nome)

  const formatCurrency = (value: number) => {
    if (value >= 1000000) {
      return `R$ ${(value / 1000000).toFixed(1)}M`
    }
    if (value >= 1000) {
      return `R$ ${(value / 1000).toFixed(0)}K`
    }
    return `R$ ${value.toLocaleString('pt-BR')}`
  }

  const totals = empreendimentos.reduce(
    (acc, e) => ({
      leads: acc.leads + (e.total_leads || 0),
      vendas: acc.vendas + (e.vendas || 0),
      vgv: acc.vgv + (e.vgv || 0),
    }),
    { leads: 0, vendas: 0, vgv: 0 }
  )

  if (loading) {
    return (
      <div className={styles.page}>
        <div className={styles.loading}>
          <div className={styles.spinner}></div>
          <p>Carregando empreendimentos...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHeader}>
          <div>
            <h1 className={styles.pageTitle}>Performance por Empreendimento</h1>
            <p className={styles.pageSubtitle}>Analise de desempenho por empreendimento</p>
          </div>
        </div>
        <div className={styles.errorState}>
          <p>{error}</p>
          <p>Verifique se a API esta funcionando corretamente.</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div>
          <h1 className={styles.pageTitle}>Performance por Empreendimento</h1>
          <p className={styles.pageSubtitle}>Analise comparativa de resultados por produto</p>
        </div>
      </div>

      {/* KPIs Gerais */}
      <section className={styles.section}>
        <div className={styles.kpiGrid}>
          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.primary}`}>
              <Building2 size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Empreendimentos Ativos</span>
              <span className={styles.kpiValue}>{empreendimentos.length}</span>
            </div>
          </div>
          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.secondary}`}>
              <Target size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Total Leads</span>
              <span className={styles.kpiValue}>{totals.leads.toLocaleString('pt-BR')}</span>
            </div>
          </div>
          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.success}`}>
              <TrendingUp size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>Total Vendas</span>
              <span className={styles.kpiValue}>{totals.vendas.toLocaleString('pt-BR')}</span>
            </div>
          </div>
          <div className={styles.kpiCard}>
            <div className={`${styles.kpiIcon} ${styles.warning}`}>
              <DollarSign size={24} />
            </div>
            <div className={styles.kpiContent}>
              <span className={styles.kpiLabel}>VGV Total</span>
              <span className={styles.kpiValue}>{formatCurrency(totals.vgv)}</span>
            </div>
          </div>
        </div>
      </section>

      {/* FAJ INVEST */}
      <section className={styles.section}>
        <div className={styles.sectionHeaderWithBadge}>
          <h2 className={styles.sectionTitle}>
            <span className={`${styles.linhaBadge} ${styles.invest}`}>FAJ INVEST</span>
            Smart Studios & Investimentos
          </h2>
        </div>
        <div className={styles.empreendimentoGrid}>
          {investEmpreendimentos.length > 0 ? (
            investEmpreendimentos.map((emp) => (
              <div
                key={emp.id}
                className={`${styles.empreendimentoCard} ${styles.invest}`}
                onClick={() => handleCardClick(emp)}
              >
                <div className={styles.empreendimentoHeader}>
                  <div className={styles.empreendimentoInfo}>
                    <h3 className={styles.empreendimentoName}>{emp.nome}</h3>
                    <span className={`${styles.empreendimentoStatus} ${styles.success}`}>
                      Ativo
                    </span>
                  </div>
                  <ChevronRight size={20} className={styles.chevron} />
                </div>
                <div className={styles.empreendimentoKpis}>
                  <div className={styles.empKpi}>
                    <span className={styles.empKpiLabel}>Leads</span>
                    <span className={styles.empKpiValue}>{(emp.total_leads || 0).toLocaleString('pt-BR')}</span>
                  </div>
                  <div className={styles.empKpi}>
                    <span className={styles.empKpiLabel}>Vendas</span>
                    <span className={styles.empKpiValue}>{(emp.vendas || 0).toLocaleString('pt-BR')}</span>
                  </div>
                  <div className={styles.empKpi}>
                    <span className={styles.empKpiLabel}>VGV</span>
                    <span className={styles.empKpiValue}>{formatCurrency(emp.vgv || 0)}</span>
                  </div>
                  <div className={styles.empKpi}>
                    <span className={styles.empKpiLabel}>Conversao</span>
                    <span className={`${styles.empKpiValue} ${styles.success}`}>
                      {(emp.taxa_conversao || 0).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.emptyState}>Nenhum empreendimento FAJ INVEST encontrado</div>
          )}
        </div>
      </section>

      {/* FAJ REALIZA */}
      <section className={styles.section}>
        <div className={styles.sectionHeaderWithBadge}>
          <h2 className={styles.sectionTitle}>
            <span className={`${styles.linhaBadge} ${styles.realiza}`}>FAJ REALIZA</span>
            Minha Casa Minha Vida
          </h2>
        </div>
        <div className={styles.empreendimentoGrid}>
          {realizaEmpreendimentos.length > 0 ? (
            realizaEmpreendimentos.map((emp) => (
              <div
                key={emp.id}
                className={`${styles.empreendimentoCard} ${styles.realiza}`}
                onClick={() => handleCardClick(emp)}
              >
                <div className={styles.empreendimentoHeader}>
                  <div className={styles.empreendimentoInfo}>
                    <h3 className={styles.empreendimentoName}>{emp.nome}</h3>
                    <span className={`${styles.empreendimentoStatus} ${styles.success}`}>
                      Ativo
                    </span>
                  </div>
                  <ChevronRight size={20} className={styles.chevron} />
                </div>
                <div className={styles.empreendimentoKpis}>
                  <div className={styles.empKpi}>
                    <span className={styles.empKpiLabel}>Leads</span>
                    <span className={styles.empKpiValue}>{(emp.total_leads || 0).toLocaleString('pt-BR')}</span>
                  </div>
                  <div className={styles.empKpi}>
                    <span className={styles.empKpiLabel}>Vendas</span>
                    <span className={styles.empKpiValue}>{(emp.vendas || 0).toLocaleString('pt-BR')}</span>
                  </div>
                  <div className={styles.empKpi}>
                    <span className={styles.empKpiLabel}>VGV</span>
                    <span className={styles.empKpiValue}>{formatCurrency(emp.vgv || 0)}</span>
                  </div>
                  <div className={styles.empKpi}>
                    <span className={styles.empKpiLabel}>Conversao</span>
                    <span className={`${styles.empKpiValue} ${styles.success}`}>
                      {(emp.taxa_conversao || 0).toFixed(1)}%
                    </span>
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className={styles.emptyState}>Nenhum empreendimento FAJ REALIZA encontrado</div>
          )}
        </div>
      </section>

      {/* Tabela Comparativa */}
      <section className={styles.section}>
        <h2 className={styles.sectionTitle}>Comparativo de Performance</h2>
        <div className={styles.tableContainer}>
          <table className={styles.dataTable}>
            <thead>
              <tr>
                <th>Linha</th>
                <th>Empreendimento</th>
                <th>Leads</th>
                <th>Vendas</th>
                <th>Taxa Conversao</th>
                <th>VGV</th>
              </tr>
            </thead>
            <tbody>
              {empreendimentos.filter(e => e.nome).map((emp) => (
                <tr key={emp.id}>
                  <td>
                    <span className={`${styles.linhaBadgeSm} ${emp.tipo_negocio === 'invest' ? styles.invest : styles.realiza}`}>
                      {emp.tipo_negocio === 'invest' ? 'INVEST' : 'REALIZA'}
                    </span>
                  </td>
                  <td className={styles.nameCell}>{emp.nome}</td>
                  <td>{(emp.total_leads || 0).toLocaleString('pt-BR')}</td>
                  <td className={styles.vendasCell}>{(emp.vendas || 0).toLocaleString('pt-BR')}</td>
                  <td>
                    <span className={`${styles.conversionBadge} ${(emp.taxa_conversao || 0) >= 5 ? styles.success : styles.warning}`}>
                      {(emp.taxa_conversao || 0).toFixed(1)}%
                    </span>
                  </td>
                  <td>{formatCurrency(emp.vgv || 0)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr>
                <td></td>
                <td><strong>TOTAL</strong></td>
                <td><strong>{totals.leads.toLocaleString('pt-BR')}</strong></td>
                <td><strong>{totals.vendas.toLocaleString('pt-BR')}</strong></td>
                <td>
                  <span className={`${styles.conversionBadge} ${styles.success}`}>
                    {totals.leads > 0 ? ((totals.vendas / totals.leads) * 100).toFixed(1) : '0.0'}%
                  </span>
                </td>
                <td><strong>{formatCurrency(totals.vgv)}</strong></td>
              </tr>
            </tfoot>
          </table>
        </div>
      </section>

      {/* Modal de Detalhes */}
      {selectedEmp && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <div className={styles.modalTitleArea}>
                <span className={`${styles.linhaBadge} ${selectedEmp.tipo_negocio === 'invest' ? styles.invest : styles.realiza}`}>
                  {selectedEmp.tipo_negocio === 'invest' ? 'FAJ INVEST' : 'FAJ REALIZA'}
                </span>
                <h2 className={styles.modalTitle}>{selectedEmp.nome}</h2>
              </div>
              <button className={styles.modalClose} onClick={closeModal}>
                <X size={24} />
              </button>
            </div>

            {loadingDetalhes ? (
              <div className={styles.modalLoading}>
                <div className={styles.spinner}></div>
                <p>Carregando detalhes...</p>
              </div>
            ) : empDetalhes ? (
              <div className={styles.modalContent}>
                {/* KPIs do Empreendimento */}
                <div className={styles.modalKpis}>
                  <div className={styles.modalKpiCard}>
                    <div className={`${styles.modalKpiIcon} ${styles.primary}`}>
                      <Users size={20} />
                    </div>
                    <div className={styles.modalKpiContent}>
                      <span className={styles.modalKpiLabel}>Total Leads</span>
                      <span className={styles.modalKpiValue}>
                        {(empDetalhes.metricas.total_leads || 0).toLocaleString('pt-BR')}
                      </span>
                    </div>
                  </div>
                  <div className={styles.modalKpiCard}>
                    <div className={`${styles.modalKpiIcon} ${styles.success}`}>
                      <TrendingUp size={20} />
                    </div>
                    <div className={styles.modalKpiContent}>
                      <span className={styles.modalKpiLabel}>Vendas</span>
                      <span className={styles.modalKpiValue}>
                        {(empDetalhes.metricas.total_vendas || 0).toLocaleString('pt-BR')}
                      </span>
                    </div>
                  </div>
                  <div className={styles.modalKpiCard}>
                    <div className={`${styles.modalKpiIcon} ${styles.warning}`}>
                      <DollarSign size={20} />
                    </div>
                    <div className={styles.modalKpiContent}>
                      <span className={styles.modalKpiLabel}>VGV</span>
                      <span className={styles.modalKpiValue}>
                        {formatCurrency(empDetalhes.metricas.vgv || 0)}
                      </span>
                    </div>
                  </div>
                  <div className={styles.modalKpiCard}>
                    <div className={`${styles.modalKpiIcon} ${styles.secondary}`}>
                      <Target size={20} />
                    </div>
                    <div className={styles.modalKpiContent}>
                      <span className={styles.modalKpiLabel}>Conversao</span>
                      <span className={styles.modalKpiValue}>
                        {(empDetalhes.metricas.taxa_conversao || 0).toFixed(1)}%
                      </span>
                    </div>
                  </div>
                </div>

                {/* Informacoes adicionais */}
                <div className={styles.modalMetricsRow}>
                  <div className={styles.modalMetric}>
                    <span className={styles.modalMetricLabel}>Ticket Medio</span>
                    <span className={styles.modalMetricValue}>
                      {formatCurrency(empDetalhes.metricas.ticket_medio || 0)}
                    </span>
                  </div>
                  <div className={styles.modalMetric}>
                    <span className={styles.modalMetricLabel}>Pre-cadastros</span>
                    <span className={styles.modalMetricValue}>
                      {(empDetalhes.metricas.total_pre_cadastros || 0).toLocaleString('pt-BR')}
                    </span>
                  </div>
                </div>

                <div className={styles.modalSections}>
                  {/* Leads por Situacao */}
                  <div className={styles.modalSection}>
                    <h3 className={styles.modalSectionTitle}>
                      <BarChart3 size={18} />
                      Leads por Situacao
                    </h3>
                    <div className={styles.modalList}>
                      {empDetalhes.leads_por_situacao.slice(0, 6).map((item, idx) => (
                        <div key={idx} className={styles.modalListItem}>
                          <span className={styles.modalListLabel}>{item.situacao}</span>
                          <span className={styles.modalListValue}>
                            {(item.quantidade || 0).toLocaleString('pt-BR')}
                          </span>
                        </div>
                      ))}
                      {empDetalhes.leads_por_situacao.length === 0 && (
                        <p className={styles.modalEmpty}>Sem dados de situacao</p>
                      )}
                    </div>
                  </div>

                  {/* Top Corretores */}
                  <div className={styles.modalSection}>
                    <h3 className={styles.modalSectionTitle}>
                      <Award size={18} />
                      Top Corretores
                    </h3>
                    <div className={styles.modalList}>
                      {empDetalhes.top_corretores.map((corretor, idx) => (
                        <div key={idx} className={styles.modalListItem}>
                          <div className={styles.corretorInfo}>
                            <span className={styles.corretorRank}>#{idx + 1}</span>
                            <span className={styles.corretorName}>{corretor.nome}</span>
                          </div>
                          <div className={styles.corretorStats}>
                            <span className={styles.corretorVendas}>{corretor.vendas} vendas</span>
                            <span className={styles.corretorVgv}>{formatCurrency(corretor.vgv)}</span>
                          </div>
                        </div>
                      ))}
                      {empDetalhes.top_corretores.length === 0 && (
                        <p className={styles.modalEmpty}>Sem dados de corretores</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div className={styles.modalError}>
                <p>Erro ao carregar detalhes do empreendimento</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
